import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, post } from '@yingbaba/api';
import { Button, ButtonLink, ErrorNotice, Notice } from '@yingbaba/ui';
type Confirmation = { status: string; device: string; login_code: string; expires_at: string };

export function WechatConfirm() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [view, setView] = useState<Confirmation | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const pending = useRef(false);
  const scope = useRef(token);
  scope.current = token;
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setView(null);
    setError(null);
    setLoading(true);
    if (!/^[a-f0-9]{64}$/.test(token)) {
      setError(new Error('确认链接无效，请回到电脑重新扫码。'));
      setLoading(false);
      return;
    }
    api<Confirmation>(`/auth/wechat/official/confirmation?token=${encodeURIComponent(token)}`, {
      signal: controller.signal,
    })
      .then((v) => {
        if (active) setView(v);
      })
      .catch((e) => {
        if (active) setError(e);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [token, retry]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const expired = view && now >= Date.parse(view.expires_at);
  async function choose(decision: 'confirm' | 'deny') {
    if (pending.current || !view || expired) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    const current = token;
    try {
      const result = await post<Confirmation>('/auth/wechat/official/confirmation', {
        token,
        decision,
      });
      if (scope.current === current) setView(result);
    } catch (e) {
      if (scope.current === current) setError(e);
    } finally {
      pending.current = false;
      if (scope.current === current) setBusy(false);
    }
  }
  return (
    <section className="container section">
      <div className="panel stack wechat-confirm-card">
        <p className="eyebrow">影爸爸 · 微信登录确认</p>
        <h1>确认本次电脑登录</h1>
        {loading ? (
          <p role="status">正在核对登录请求…</p>
        ) : view ? (
          <>
            <div className="wechat-confirm-device">
              <p>{view.device}</p>
              <p>电脑识别码</p>
              <strong className="wechat-login-code">{view.login_code}</strong>
            </div>
            {view.status === 'confirmed' ? (
              <Notice>已确认，请返回电脑继续登录或绑定账号。此页面不会登录你的手机浏览器。</Notice>
            ) : view.status === 'denied' ? (
              <Notice>本次登录已拒绝或取消。需要登录时，请在电脑重新获取二维码。</Notice>
            ) : expired ? (
              <Notice>本次登录请求已过期，请在电脑重新扫码。</Notice>
            ) : (
              <>
                <p>
                  请确认这是你刚刚发起的登录，并核对电脑上显示的识别码。如果二维码由他人发送或转发，请拒绝登录。
                </p>
                <Button loading={busy} onClick={() => void choose('confirm')}>
                  确认登录此电脑
                </Button>
                <Button variant="secondary" disabled={busy} onClick={() => void choose('deny')}>
                  不是本人操作，拒绝登录
                </Button>
              </>
            )}
          </>
        ) : null}
        <ErrorNotice error={error} />
        {!loading && !view && (
          <Button variant="secondary" onClick={() => setRetry((v) => v + 1)}>
            重新核对链接
          </Button>
        )}
        <ButtonLink to="/" variant="ghost">
          返回影爸爸首页
        </ButtonLink>
      </div>
    </section>
  );
}
